import { toast } from "@/hooks/use-toast";

// Define webhook URLs for different forms
const WEBHOOK_URLS = {
  home_page_contact_form: "https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjYwNTY5MDYzZjA0M2Q1MjZlNTUzMzUxMzMi_pc",
  contact_page_form: "https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjYwNTY5MDYzZjA0M2Q1MjZlNTUzMTUxMzEi_pc",
  // Add other forms as needed with their respective webhook URLs
  default: "https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjYwNTY5MDYzZjA0M2Q1MjZlNTUzMTUxMzEi_pc" // Fallback webhook
};

/**
 * Sends form data to a Pabbly Connect webhook
 * @param formData The form data to send
 * @param formName The name of the form that was submitted (for identification in Pabbly)
 * @param webhookUrl Optional override for the webhook URL
 * @returns Promise that resolves when the submission is complete
 */
export const submitToPabbly = async (
  formData: Record<string, any>,
  formName: string,
  webhookUrl?: string
) => {
  try {
    // Determine which webhook URL to use
    const targetWebhookUrl = webhookUrl || WEBHOOK_URLS[formName as keyof typeof WEBHOOK_URLS] || WEBHOOK_URLS.default;
    
    // Create a new object for processed data to avoid mutating the original
    const processedData = { ...formData };
    
    // If services array exists, convert it to a comma-separated string
    // and replace the original services array with the string version
    if (Array.isArray(processedData.services)) {
      // Replace dashes with spaces in each service ID before joining
      const servicesString = processedData.services
        .map(service => service.replace(/-/g, ' ')) // Replace dashes with spaces
        .join(", ");
        
      // Add as selectedServices but also replace the original array
      // to ensure we don't send the array structure to Pabbly
      processedData.selectedServices = servicesString;
      delete processedData.services; // Remove the original array
    }
    
    // If there's a name field but no firstName/lastName (for backward compatibility),
    // split it into firstName and lastName
    if (processedData.name && !processedData.firstName && !processedData.lastName) {
      const nameParts = processedData.name.split(' ');
      processedData.firstName = nameParts[0] || '';
      processedData.lastName = nameParts.slice(1).join(' ') || '';
      // Keep the original name field for backwards compatibility
    }

    // Gather additional metadata for traceability
    const submission_id = (typeof crypto !== "undefined" && typeof (crypto as any).randomUUID === "function")
      ? (crypto as any).randomUUID()
      : `sub_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    const params = new URLSearchParams(window.location.search || "");
    const utm_source = params.get("utm_source") || "";
    const utm_medium = params.get("utm_medium") || "";
    const utm_campaign = params.get("utm_campaign") || "";
    const utm_term = params.get("utm_term") || "";
    const utm_content = params.get("utm_content") || "";

    const user_agent = navigator.userAgent;
    const referrer = document.referrer;

    // Include form name and metadata
    const dataToSend = {
      ...processedData,
      form_name: formName,
      submission_id,
      submission_date: new Date().toISOString(),
      page_url: window.location.href,
      user_agent,
      referrer,
      utm_source,
      utm_medium,
      utm_campaign,
      utm_term,
      utm_content,
    };

    console.log(`Submitting form data to Pabbly Connect: ${formName}`, dataToSend);
    console.log(`Using webhook URL: ${targetWebhookUrl}`);

    // Add a timeout to avoid hanging requests
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 10000);

    try {
      await fetch(targetWebhookUrl, {
        method: "POST",
        // Intentionally omit Content-Type header for no-cors simple request
        body: JSON.stringify(dataToSend),
        mode: "no-cors", // This is important for cross-origin requests
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeoutId);
    }

    // Show success toast
    toast({
      title: "Form submitted!",
      description: "Thank you! Your submission was received.",
    });

    return true;
  } catch (error) {
    console.error("Error submitting form to Pabbly Connect:", error);
    
    // Show error toast
    toast({
      title: "Submission error",
      description: "There was a problem submitting your form. Please try again.",
      variant: "destructive",
    });
    
    return false;
  }
};
